import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  AppBar,
  Toolbar,
  useTheme,
} from '@mui/material';
import {
  Add,
  Search,
  Person,
  Article,
  Visibility,
  Analytics as AnalyticsIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckIcon,
  Upload as UploadIcon,
  LocalShipping as EquipmentIcon,
  Assessment as ReportsIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { postApi, userApi } from '../services/api';
import { Post, User } from '../types';

const PostsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [postsData, usersData] = await Promise.all([
        postApi.getPosts(),
        userApi.getUsers(),
      ]);
      setPosts(postsData);
      setUsers(usersData);
    } catch (err) {
      setError('Failed to load posts. Make sure the Spring Boot backend is running on port 8080.');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (userId: number): string => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Unknown User';
  };

  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getUserName(post.userId).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const navItems = [
    { label: 'Dashboard', icon: <AnalyticsIcon sx={{ fontSize: 18, mr: 0.5 }} />, path: '/', active: false },
    { label: 'Load Locations', icon: <LocationIcon sx={{ fontSize: 18, mr: 0.5 }} />, path: '/posts', active: true },
    { label: 'Dump Locations', icon: <LocationIcon sx={{ fontSize: 18, mr: 0.5 }} />, path: '/posts' },
    { label: 'Materials', icon: <CheckIcon sx={{ fontSize: 18, mr: 0.5 }} />, path: '/external' },
    { label: 'Operators', icon: <PeopleIcon sx={{ fontSize: 18, mr: 0.5 }} />, path: '/users' },
    { label: 'Equipment', icon: <EquipmentIcon sx={{ fontSize: 18, mr: 0.5 }} />, path: '/users' },
    { label: 'Plan Uploads', icon: <UploadIcon sx={{ fontSize: 18, mr: 0.5 }} />, path: '/posts' },
    { label: 'Reports', icon: <ReportsIcon sx={{ fontSize: 18, mr: 0.5 }} />, path: '/posts' },
  ];

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
        {/* Navigation Bar */}
        <AppBar position="static" elevation={0} sx={{ backgroundColor: '#2d3e50', borderBottom: '1px solid #475569' }}>
          <Toolbar sx={{ minHeight: 64, px: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
              <Box sx={{ 
                width: 48, 
                height: 48, 
                backgroundColor: '#667eea', 
                borderRadius: 1.5, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mr: 1.5
              }}>
                <EquipmentIcon sx={{ fontSize: 28, color: theme.palette.text.primary }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 700, lineHeight: 1.2 }}>
                  MineFlow
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
                  Engineering Portal
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
              {navItems.map((item, index) => (
                <Button
                  key={index}
                  onClick={() => navigate(item.path)}
                  sx={{
                    color: item.active ? 'white' : '#94a3b8',
                    backgroundColor: item.active ? '#667eea' : 'transparent',
                    borderRadius: 1.5,
                    px: 2,
                    py: 1,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: item.active ? '#4c5fd7' : 'rgba(249, 115, 22, 0.1)',
                      color: item.active ? 'white' : '#667eea',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 'auto',
                  }}
                >
                  {item.icon}
                  {item.label}
                </Button>
              ))}
            </Box>
          </Toolbar>
        </AppBar>
        
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress sx={{ color: '#667eea' }} />
          <Typography variant="h6" sx={{ ml: 2, color: theme.palette.text.primary }}>
            Loading posts...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      {/* Navigation Bar */}
      <AppBar position="static" elevation={0} sx={{ backgroundColor: '#2d3e50', borderBottom: '1px solid #475569' }}>
        <Toolbar sx={{ minHeight: 64, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <Box sx={{ 
              width: 48, 
              height: 48, 
              backgroundColor: '#667eea', 
              borderRadius: 1.5, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mr: 1.5
            }}>
              <EquipmentIcon sx={{ fontSize: 28, color: theme.palette.text.primary }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 700, lineHeight: 1.2 }}>
                MineFlow
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
                Engineering Portal
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
            {navItems.map((item, index) => (
              <Button
                key={index}
                onClick={() => navigate(item.path)}
                sx={{
                  color: item.active ? 'white' : '#94a3b8',
                  backgroundColor: item.active ? '#667eea' : 'transparent',
                  borderRadius: 1.5,
                  px: 2,
                  py: 1,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: item.active ? '#4c5fd7' : 'rgba(249, 115, 22, 0.1)',
                    color: item.active ? 'white' : '#667eea',
                  },
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 'auto',
                }}
              >
                {item.icon}
                {item.label}
              </Button>
            ))}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ px: 3, py: 4, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.text.primary }}>
          Reports & Updates
        </Typography>
        <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
          Create, edit, and manage operational reports and updates.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, border: '1px solid #ef4444' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search reports by title, content, or author..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
                sx: { backgroundColor: theme.palette.background.default, color: theme.palette.text.primary }
              }}
              sx={{ 
                flexGrow: 1, 
                minWidth: 250,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: '#64748b' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea' },
                },
                '& .MuiInputBase-input': { color: theme.palette.text.primary }
              }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => alert('Report creation requires backend integration')}
              sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' }
              }}
            >
              Add Report
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, 
        gap: 3,
        mb: 4
      }}>
        {filteredPosts.length === 0 ? (
          <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 8 }}>
            <Article sx={{ fontSize: 64, color: theme.palette.text.secondary, mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 1, color: theme.palette.text.secondary }}>
              No reports found
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.secondary }}>
              {searchTerm ? 'Try adjusting your search terms' : 'Start the Spring Boot backend to load reports'}
            </Typography>
          </Box>
        ) : (
          filteredPosts.map((post) => (
            <Card
              key={post.id}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme.palette.background.paper,
                borderRadius: 2,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.3)'
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#06beb6', mr: 2 }}>
                    <Article />
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="h2" noWrap sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                      {post.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      by {getUserName(post.userId)}
                    </Typography>
                  </Box>
                </Box>
                
                <Typography 
                  variant="body2" 
                  sx={{
                    color: theme.palette.text.secondary,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    mb: 2
                  }}
                >
                  {post.content}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip 
                    icon={<Person />} 
                    label={getUserName(post.userId)} 
                    size="small" 
                    sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.secondary, borderColor: theme.palette.divider }}
                    variant="outlined" 
                  />
                  <Chip 
                    icon={<Visibility />} 
                    label={`${Math.floor(Math.random() * 100)} views`}
                    size="small" 
                    sx={{ backgroundColor: theme.palette.background.default, color: '#06beb6', borderColor: '#06beb6' }}
                    variant="outlined" 
                  />
                </Box>
                
                {post.createdAt && (
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Created: {new Date(post.createdAt).toLocaleDateString()}
                  </Typography>
                )}
              </CardContent>
              
              <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                <Button size="small" sx={{ color: '#667eea', fontWeight: 600 }}>Read More</Button>
              </CardActions>
            </Card>
          ))
        )}
      </Box>

      {filteredPosts.length > 0 && (
        <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
              Statistics
            </Typography>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h4" sx={{ color: '#667eea', fontWeight: 700 }}>
                  {filteredPosts.length}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Total Reports
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" sx={{ color: '#06beb6', fontWeight: 700 }}>
                  {new Set(filteredPosts.map(p => p.userId)).size}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Active Authors
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
      </Box>
    </Box>
  );
};

export default PostsPage;